// Pure, dependency-free logic for the Applicants module. Kept separate from the
// React components and the Cloud Functions so it can be unit-tested directly.
// The gfunctions/extension functions mirror this logic (separate Node deployment,
// cannot import from src/), so keep the two in sync when changing the rules.
//
// The required-doc checklist is config-driven (config/applicants): callers pass
// the list resolved via ApplicantConfigService. The constants below are only a
// fallback when no list is supplied (older callers + unit tests).
import {
  APPLICANT_REQUIRED_DOCS_STAGE1,
  APPLICANT_REQUIRED_DOCS_STAGE2,
} from "../../constants";
import { normalizeNit } from "../entities/entityLogic";

// Fields every stored document (file) must carry (Property 9: complete metadata).
export const REQUIRED_DOC_FIELDS = [
  "path",
  "type",
  "version",
  "hash",
  "status",
  "author",
  "uploadedAt",
];

// Fallback required-document checklist for a stage (1 = postulación, 2 = post-selección).
export function requiredDocsForStage(stage = 1) {
  return stage === 2 ? APPLICANT_REQUIRED_DOCS_STAGE2 : APPLICANT_REQUIRED_DOCS_STAGE1;
}

// A documents[type] entry is either the new multi-file shape ({ files: [...] })
// or a legacy single-file entry (metadata at the top level). Normalize to always
// expose an files[] array.
export function normalizeDoc(entry) {
  if (!entry || typeof entry !== "object") return { files: [] };
  if (Array.isArray(entry.files)) return entry;
  if (entry.path) return { ...entry, files: [entry] }; // legacy single file
  return { ...entry, files: [] };
}

export function docFiles(entry) {
  return normalizeDoc(entry).files.filter((a) => a && a.path);
}

export function docHasFile(entry) {
  return docFiles(entry).length > 0;
}

function docAllValidated(entry) {
  const arch = docFiles(entry);
  return arch.length > 0 && arch.every((a) => a.status === "VALIDATED");
}

// Rollup status for a single documents[type] entry (used by the validation UI).
export function docStatusRollup(entry) {
  const arch = docFiles(entry);
  if (arch.length === 0) return "PENDING";
  if (arch.some((a) => a.status === "REJECTED")) return "REJECTED";
  if (arch.every((a) => a.status === "VALIDATED")) return "VALIDATED";
  return "UPLOADED";
}

// Completitud = 80% weighted on the stage checklist (by presence of an uploaded
// file) + 20% on the core personal fields being present.
export function computeCompletion(applicant = {}, documents = {}, stage = 1, requiredDocs = null) {
  const required = Array.isArray(requiredDocs) ? requiredDocs : requiredDocsForStage(stage);
  const personalOk = !!(
    applicant.nuip &&
    applicant.firstName &&
    applicant.lastName &&
    applicant.email
  );
  const have = required.filter((t) => docHasFile(documents[t])).length;
  const docPart = required.length ? (have / required.length) * 80 : 80;
  const personalPart = personalOk ? 20 : 0;
  return Math.round(docPart + personalPart);
}

// documentationStatus transition after a validation change:
//  - VALIDATED  : every required document (for the stage) is present AND all its files VALIDATED
//  - INCOMPLETE: at least one file was REJECTED
//  - COMPLETE  : otherwise (uploaded but not yet fully validated)
export function recomputeStatus(documents = {}, stage = 1, requiredDocs = null) {
  const required = Array.isArray(requiredDocs) ? requiredDocs : requiredDocsForStage(stage);
  const allValidated = required.length > 0 && required.every((t) => docAllValidated(documents[t]));
  if (allValidated) return "VALIDATED";
  const anyRejected = Object.values(documents).some((entry) =>
    docFiles(entry).some((a) => a.status === "REJECTED")
  );
  return anyRejected ? "INCOMPLETE" : "COMPLETE";
}

// --- Dónde aterriza un archivo que se sube ----------------------------------
// Subir un archivo es una de dos cosas, y el aspirante tiene que poder decir
// cuál: REEMPLAZAR uno concreto de los que ya cargó, o AGREGAR uno nuevo. Antes
// no existía esa distinción — el intake agregaba mientras cupiera y versionaba el
// último cuando ya no — así que «Reemplazar archivo» dejaba intacto el archivo
// rechazado y el documento se quedaba en RECHAZADO para siempre.
//
// `replaceFileId` nombra el archivo que se reemplaza. Reemplazar versiona ESE
// espacio: el anterior pasa a versions[] y el nuevo hereda su id, de modo que el
// documento sigue teniendo un solo archivo y la decisión previa (validación o
// rechazo) queda anulada — el que reemplaza vuelve a UPLOADED.
//
// `files` son los archivos ya guardados del tipo; `entryId` acota a los de UN
// registro (formación / experiencia) en los documentos atados, donde el cupo se
// cuenta por registro. Devuelve el espacio resuelto o { error }.
export function resolveUploadSlot(files, { entryId = null, replaceFileId = null, maxFiles = 1 } = {}) {
  const list = Array.isArray(files) ? files : [];
  const scoped = entryId ? list.filter((a) => a && a.entryId === entryId) : list;

  if (replaceFileId) {
    // El archivo señalado tiene que existir Y pertenecer al mismo registro: un id
    // de otra formación reapuntaría el diploma de un tercero.
    const target = scoped.find((a) => a && a.id === replaceFileId);
    if (!target) return { error: "UNKNOWN_REPLACE_TARGET" };
    const versions = Array.isArray(target.versions) ? [...target.versions] : [];
    // Se conserva por qué se fue: una versión que quedó rechazada explica el
    // reemplazo, y sin esto el motivo se perdería al limpiarlo del archivo nuevo.
    if (target.path) {
      versions.push({
        path: target.path,
        hash: target.hash,
        uploadedAt: target.uploadedAt || null,
        status: target.status || null,
        rejectionComments: target.rejectionComments || null,
      });
    }
    return {
      fileId: target.id,
      version: (target.version || 1) + 1,
      versions,
      replaceIndex: list.indexOf(target),
    };
  }

  // Agregar: sólo mientras quede cupo. Al llegar al tope hay que decir cuál se
  // reemplaza — versionar el último «por descarte» es justo la ambigüedad que
  // hacía que reemplazar no reemplazara.
  if (scoped.length >= maxFiles) return { error: "DOC_AT_MAX_FILES" };
  return { fileId: null, version: 1, versions: [], replaceIndex: -1 };
}

// --- Perfil: formación académica + experiencia laboral ----------------------
// Both are repeatable lists: an applicant may register several degrees and
// several jobs. Each entry only counts in the evaluation when it is backed by an
// uploaded supporting document (acta de grado / certificado de experiencia),
// which the submission forms surface as a warning.

// Upper bound per list, enforced on both the client and the intake so a crafted
// payload can't grow an applicant document without bound.
export const MAX_PROFILE_ENTRIES = 15;

const entryText = (v) => String(v ?? "").trim();
// Checkboxes arrive as booleans from the forms, but a hand-crafted payload (or a
// value round-tripped through a form field) may carry the string.
const entryFlag = (v) => v === true || v === "true";

// Stable id per formación / experiencia entry. It is what ties an uploaded
// certificate to the entry it certifies (file.entryId), so it must survive every
// round trip through the form and must not be the array index — reordering or
// deleting an entry would silently re-point somebody else's diploma.
export const genEntryId = () =>
  Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 4);

// Maps a raw list onto a fixed set of fields: `text` are trimmed strings, `flags`
// are booleans. Entries whose text fields all ended up blank are dropped — a row
// where only a checkbox was ticked carries no information. `finish` applies the
// cross-field consistency rules of each list. Shared by both normalizers below.
function normalizeEntries(value, spec, finish) {
  if (!Array.isArray(value)) return [];
  return value
    .filter((e) => e && typeof e === "object")
    .map((e) => {
      const out = { id: entryText(e.id) };
      spec.text.forEach((f) => { out[f] = entryText(e[f]); });
      spec.flags.forEach((f) => { out[f] = entryFlag(e[f]); });
      return finish ? finish(out, e) : out;
    })
    // `id` deliberately doesn't count: an entry carrying only an id is blank.
    .filter((e) => spec.text.some((f) => e[f]))
    .map((e) => (e.id ? e : { ...e, id: genEntryId() }))
    .slice(0, MAX_PROFILE_ENTRIES);
}

// `inProgress`: estudios en curso — no graduation date is expected.
// `foreignDegree` / `convalidated`: a degree obtained abroad must state whether
// it has been convalidated in Colombia (the resolución rides with the acta de
// grado support). `convalidated` is meaningless without `foreignDegree`.
const EDUCATION_SPEC = {
  text: ["level", "degree", "institution", "graduationDate"],
  flags: ["inProgress", "foreignDegree", "convalidated"],
};
// `current`: the applicant still works there — no end date is expected.
// `nit`: the employer's NIT, digits only (same rule as the entities module).
const EXPERIENCE_SPEC = {
  text: ["position", "company", "nit", "startDate", "endDate", "description"],
  flags: ["current"],
};

// One company certificate often covers several roles / date ranges, so an
// experience entry can carry `periods[]`. When it does, the entry's own
// position/startDate/endDate/current become a SUMMARY of its periods, which is
// what every reader written before periods existed keeps using. Entries without
// periods (everything stored before this change) are one implicit period.
export const MAX_EXPERIENCE_PERIODS = 20;
const PERIOD_SPEC = { text: ["position", "startDate", "endDate"], flags: ["current"] };
// Dates arrive as ISO datetimes or YYYY-MM-DD; the day part orders both.
const dayOf = (v) => String(v || "").slice(0, 10);
const byDay = (a, b) => dayOf(a).localeCompare(dayOf(b));

export function normalizePeriods(value) {
  if (!Array.isArray(value)) return [];
  return value
    .filter((p) => p && typeof p === "object")
    .map((p) => {
      const out = {};
      PERIOD_SPEC.text.forEach((f) => { out[f] = entryText(p[f]); });
      PERIOD_SPEC.flags.forEach((f) => { out[f] = entryFlag(p[f]); });
      if (out.current) out.endDate = "";
      return out;
    })
    .filter((p) => PERIOD_SPEC.text.some((f) => p[f]))
    .slice(0, MAX_EXPERIENCE_PERIODS);
}

// Earliest start, latest end (none while any period is current), and the role
// of the most recent period that names one.
export function summarizeExperience(entry) {
  const periods = entry && entry.periods;
  if (!Array.isArray(periods) || periods.length === 0) return entry;
  const starts = periods.map((p) => p.startDate).filter(Boolean).sort(byDay);
  const ends = periods.map((p) => p.endDate).filter(Boolean).sort(byDay);
  const current = periods.some((p) => p.current);
  const named = periods.filter((p) => p.position).sort((a, b) => byDay(a.startDate, b.startDate));
  return {
    ...entry,
    position: named.length ? named[named.length - 1].position : entry.position,
    startDate: starts[0] || "",
    endDate: current ? "" : (ends[ends.length - 1] || ""),
    current,
  };
}

// The periods of an entry: its own list, or the entry itself as the single
// period of a legacy (pre-periods) entry.
export function experiencePeriods(entry) {
  if (!entry) return [];
  if (Array.isArray(entry.periods) && entry.periods.length) return entry.periods;
  const { position = "", startDate = "", endDate = "", current = false } = entry;
  return position || startDate || endDate || current ? [{ position, startDate, endDate, current }] : [];
}

// --- Experience time ---------------------------------------------------------
// Worked time over any number of periods, with overlaps counted once (two
// simultaneous jobs are not twice the experience). An open period runs until
// `today`; a period without a start, or ending before it starts, counts zero.
// End dates are inclusive, and time is counted in whole calendar months
// (2018-01-01 → 2018-12-31 is 12 months).
const DAY_MS = 24 * 60 * 60 * 1000;
const toUtcDay = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dayOf(v));
  return m ? Date.UTC(+m[1], +m[2] - 1, +m[3]) : null;
};
// Whole months in [start, endExclusive).
const monthsBetween = (start, endExclusive) => {
  const a = new Date(start);
  const b = new Date(endExclusive);
  const months = (b.getUTCFullYear() - a.getUTCFullYear()) * 12 + (b.getUTCMonth() - a.getUTCMonth());
  return b.getUTCDate() >= a.getUTCDate() ? months : months - 1;
};

export function experienceMonths(periods, today = new Date()) {
  const todayMs = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  const spans = (Array.isArray(periods) ? periods : [])
    .map((p) => {
      const start = toUtcDay(p && p.startDate);
      const end = p && p.current ? todayMs : toUtcDay(p && p.endDate);
      return start != null && end != null && end >= start ? [start, end + DAY_MS] : null;
    })
    .filter(Boolean)
    .sort((a, b) => a[0] - b[0]);
  // Merge touching / overlapping spans, then count each merged span once.
  const merged = [];
  for (const [s, e] of spans) {
    const last = merged[merged.length - 1];
    if (last && s <= last[1]) last[1] = Math.max(last[1], e);
    else merged.push([s, e]);
  }
  return merged.reduce((sum, [s, e]) => sum + Math.max(0, monthsBetween(s, e)), 0);
}

// Exact length of ONE period, end date inclusive: 2020-01-15 → 2022-12-31 is
// { years: 2, months: 11, days: 17 }. An open period runs until `today`; an
// incomplete or inverted one is null.
export function periodDuration(period, today = new Date()) {
  const start = toUtcDay(period && period.startDate);
  const todayMs = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  const end = period && period.current ? todayMs : toUtcDay(period && period.endDate);
  if (start == null || end == null || end < start) return null;
  const endEx = end + DAY_MS;
  const s = new Date(start);
  const addMonths = (n) => Date.UTC(s.getUTCFullYear(), s.getUTCMonth() + n, s.getUTCDate());
  let months = Math.max(0, monthsBetween(start, endEx));
  // Month-end starts (31st) can overshoot when added; step back until they fit.
  while (months > 0 && addMonths(months) > endEx) months -= 1;
  const days = Math.round((endEx - addMonths(months)) / DAY_MS);
  return { years: Math.floor(months / 12), months: months % 12, days };
}

// { years: 2, months: 11, days: 17 } → "2 años 11 meses 17 días".
export function formatPeriodDuration(d) {
  if (!d) return "";
  const parts = [];
  if (d.years) parts.push(`${d.years} ${d.years === 1 ? "año" : "años"}`);
  if (d.months) parts.push(`${d.months} ${d.months === 1 ? "mes" : "meses"}`);
  if (d.days || parts.length === 0) parts.push(`${d.days} ${d.days === 1 ? "día" : "días"}`);
  return parts.join(" ");
}

// Total across every entry (all companies together, overlaps once).
export function totalExperienceMonths(entries, today = new Date()) {
  return experienceMonths((Array.isArray(entries) ? entries : []).flatMap(experiencePeriods), today);
}

// 27 → "2 años 3 meses"; 0 → "Menos de 1 mes".
export function formatDuration(months) {
  const n = Math.max(0, Math.floor(Number(months) || 0));
  if (n === 0) return "Menos de 1 mes";
  const y = Math.floor(n / 12);
  const m = n % 12;
  const parts = [];
  if (y) parts.push(`${y} ${y === 1 ? "año" : "años"}`);
  if (m) parts.push(`${m} ${m === 1 ? "mes" : "meses"}`);
  return parts.join(" ");
}

export const EDUCATION_FIELDS = [...EDUCATION_SPEC.text, ...EDUCATION_SPEC.flags];
export const EXPERIENCE_FIELDS = [...EXPERIENCE_SPEC.text, ...EXPERIENCE_SPEC.flags];

export function normalizeEducation(value) {
  return normalizeEntries(value, EDUCATION_SPEC, (e) => {
    // "En curso" and a graduation date are mutually exclusive; the flag wins.
    if (e.inProgress) e.graduationDate = "";
    // Convalidación only exists for a foreign degree.
    if (!e.foreignDegree) e.convalidated = false;
    return e;
  });
}

export function normalizeWorkExperience(value) {
  return normalizeEntries(value, EXPERIENCE_SPEC, (e, raw) => {
    e.nit = normalizeNit(e.nit);
    const periods = normalizePeriods(raw.periods);
    if (periods.length) return summarizeExperience({ ...e, periods });
    // "Actualmente" and an end date are mutually exclusive; the flag wins.
    if (e.current) e.endDate = "";
    return e;
  });
}

// --- Documentos de perfil ---------------------------------------------------
// A profile document belongs to the PERSON, not to a postulación: it lives on
// applicants/{nuip}.profileDocuments and is shared by every convocatoria the
// applicant enters. Two of them are "bound": each of their files certifies one
// formación / experiencia entry (file.entryId), so the same document type holds
// one file per entry. The rest (cédula and whatever else an admin adds) are
// plain per-person documents.
export const PROFILE_SECTIONS = { EDUCATION: "education", EXPERIENCE: "workExperience" };

// Canonical keys of the two bound evidence documents. They are seeded and
// protected in the profile catalog — an admin may relabel or disable them, never
// delete them or re-point what they certify.
export const EDUCATION_EVIDENCE_DOC = "education_certificate";
export const EXPERIENCE_EVIDENCE_DOC = "experience_certificate";

export const evidenceDocForSection = (section) =>
  section === PROFILE_SECTIONS.EDUCATION ? EDUCATION_EVIDENCE_DOC
    : section === PROFILE_SECTIONS.EXPERIENCE ? EXPERIENCE_EVIDENCE_DOC
      : null;

// Files of a bound document that certify one specific entry.
export function filesForEntry(docEntry, entryId) {
  if (!entryId) return [];
  return docFiles(docEntry).filter((a) => a.entryId === entryId);
}

// Whether an entry still owes its certificate. Ongoing studies and a current job
// have nothing to certify yet — no acta de grado exists for a degree in progress
// and no employer issues a final certificate for a job you still hold.
export function entryNeedsEvidence(entry, section) {
  if (!entry) return false;
  if (section === PROFILE_SECTIONS.EDUCATION) return !entry.inProgress;
  if (section === PROFILE_SECTIONS.EXPERIENCE) {
    // With periods, only an entry whose periods are ALL ongoing owes nothing yet:
    // closed periods at the same company still have to be certified.
    if (Array.isArray(entry.periods) && entry.periods.length) return entry.periods.some((p) => !p.current);
    return !entry.current;
  }
  return true;
}

// Entries of a section that owe a certificate and don't have one yet.
export function entriesMissingEvidence(entries, docEntry, section) {
  return (entries || []).filter(
    (e) => entryNeedsEvidence(e, section) && filesForEntry(docEntry, e.id).length === 0
  );
}

// Rollup for one profile document: bound documents are complete only when EVERY
// entry that owes a certificate has one, so a half-documented CV can't read as
// "Cargado". Unbound documents behave like any other (at least one file).
export function profileDocStatus(docEntry, { section = null, entries = null } = {}) {
  const files = docFiles(docEntry);
  if (section && Array.isArray(entries)) {
    const owing = entries.filter((e) => entryNeedsEvidence(e, section));
    if (owing.length === 0) return files.length ? docStatusRollup(docEntry) : "PENDING";
    if (files.some((a) => a.status === "REJECTED")) return "REJECTED";
    const covered = owing.filter((e) => filesForEntry(docEntry, e.id).length > 0);
    if (covered.length === 0) return "PENDING";
    if (covered.length < owing.length) return "UPLOADED"; // incompleto, aún cargando
    return files.every((a) => a.status === "VALIDATED") ? "VALIDATED" : "UPLOADED";
  }
  return docStatusRollup(docEntry);
}

// documentationStatus of the whole profile, over the required profile documents.
// Mirrors recomputeStatus: VALIDATED when everything required is validated,
// INCOMPLETE when something was rejected, COMPLETE otherwise.
export function recomputeProfileStatus(profileDocuments = {}, requiredDocs = [], context = {}) {
  const required = Array.isArray(requiredDocs) ? requiredDocs : [];
  // Un documento atado sin nada que certificar (sin registros, o todos en curso /
  // actuales) está satisfecho: no existe un archivo que validar. Sin esto el
  // perfil de quien no registró experiencia nunca llegaría a VALIDATED y jamás
  // podría seleccionarse — mismo criterio que usa computeProfileCompletion.
  const satisfied = (key) => {
    const { section, entries } = context[key] || {};
    if (section && Array.isArray(entries) && !entries.some((e) => entryNeedsEvidence(e, section))) {
      // Salvo que haya subido algo igualmente: entonces sí debe estar validado.
      return docFiles(profileDocuments[key]).every((a) => a.status === "VALIDATED");
    }
    return profileDocStatus(profileDocuments[key], context[key] || {}) === "VALIDATED";
  };
  const anyRejected = Object.values(profileDocuments || {}).some((entry) =>
    docFiles(entry).some((a) => a.status === "REJECTED")
  );
  if (anyRejected) return "INCOMPLETE";
  if (required.length === 0) return "COMPLETE";
  return required.every(satisfied) ? "VALIDATED" : "COMPLETE";
}

// Completitud of the profile: 80% on the required profile documents (a bound
// document counts proportionally to the entries it covers, so adding a fifth job
// without its certificate lowers the score) + 20% on the core personal fields.
export function computeProfileCompletion(applicant = {}, profileDocuments = {}, requiredDocs = [], context = {}) {
  const required = Array.isArray(requiredDocs) ? requiredDocs : [];
  const personalOk = !!(applicant.nuip && applicant.firstName && applicant.lastName && applicant.email);
  let score = 0;
  for (const key of required) {
    const { section, entries } = context[key] || {};
    const docEntry = profileDocuments[key];
    if (section && Array.isArray(entries)) {
      const owing = entries.filter((e) => entryNeedsEvidence(e, section));
      // Nothing to certify (no entries, or all of them ongoing) is not a gap.
      if (owing.length === 0) { score += 1; continue; }
      const covered = owing.filter((e) => filesForEntry(docEntry, e.id).length > 0).length;
      score += covered / owing.length;
    } else if (docHasFile(docEntry)) {
      score += 1;
    }
  }
  const docPart = required.length ? (score / required.length) * 80 : 80;
  return Math.round(docPart + (personalOk ? 20 : 0));
}

// True when a document entry carries every required metadata field with a
// non-empty value (Property 9).
export function isDocumentMetadataComplete(doc = {}, fields = REQUIRED_DOC_FIELDS) {
  return fields.every((f) => {
    const v = doc[f];
    return v !== undefined && v !== null && v !== "";
  });
}
